import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseCategoryRequestModel } from "app/based/baseApiModel/baseCategory/BaseCategoryRequestModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import dayjs from "dayjs";
import { NiceSimRegistrationInjection } from "features/niceSimRegistration/di/NiceSimRegistrationInjection";
import { NiceSimRegistrationQueryEntity } from "features/niceSimRegistration/domain/entities/NiceSimRegistrationEntity";
import { NiceSimRegistrationItemEntity } from "features/niceSimRegistration/domain/entities/NiceSimRegistrationItemEntity";
import isEmpty from "lodash/isEmpty";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import { INiceSimRegistrationObservedState } from "../interfaces/INiceSimRegistration";

export interface IDropdownActionRecord {
  record: NiceSimRegistrationItemEntity;
  action: string;
}

export type DetailSelectionType =
  | NiceSimRegistrationItemEntity
  | IDropdownActionRecord;

type FilterOptionsState = Pick<
  INiceSimRegistrationObservedState,
  "storeOptions" | "staffOptions" | "userOptions"
>;

class NiceSimRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<NiceSimRegistrationQueryEntity>(
    new NiceSimRegistrationQueryEntity(),
  );
  public fetchNumberType = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchDestiny = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchNumerology = new BehaviorSubject<BaseCategoryRequestModel>({});
  public fetchZodiac = new BehaviorSubject<BaseCategoryRequestModel>({});
  public exportExcel = new BehaviorSubject<NiceSimRegistrationQueryEntity>(
    new NiceSimRegistrationQueryEntity(),
  );
  public fetchDetail = new BehaviorSubject<NiceSimRegistrationQueryEntity>(
    new NiceSimRegistrationQueryEntity(),
  );
  public selectDetail = new BehaviorSubject<DetailSelectionType>({});
  public syncFilterOptions = new BehaviorSubject<Partial<FilterOptionsState>>(
    {},
  );
  public checkStatus = new BehaviorSubject<string>("");
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchNumberType.complete();
    this.fetchCarrier.complete();
    this.fetchDestiny.complete();
    this.fetchNumerology.complete();
    this.fetchZodiac.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.selectDetail.complete();
    this.syncFilterOptions.complete();
    this.checkStatus.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class NiceSimRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public onDetailLoaded = new BehaviorSubject<{ action?: string } | null>(null);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.onDetailLoaded.complete();
  }
}

@injectable()
export class NiceSimRegistrationViewModel extends BasedObservedViewModel<
  NiceSimRegistrationInput,
  NiceSimRegistrationOutput,
  INiceSimRegistrationObservedState
> {
  private niceSimRegistrationUseCase =
    NiceSimRegistrationInjection.getNiceSimRegistrationUseCase();
  private categoryRequestQueue: Promise<void> = Promise.resolve();

  protected override createObservedState(): INiceSimRegistrationObservedState {
    return {
      list: [],
      detail: {},
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      statusLoading: {},
      numberType: [],
      carrier: [],
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [{ label: strings.GlobalLabels.All, value: "" }],
      zodiacOptions: [],
      numerologyOptions: [],
      destinyOptions: [],
      summary: {},
    };
  }

  constructor() {
    super(new NiceSimRegistrationInput(), new NiceSimRegistrationOutput());
  }

  private enqueueCategoryRequest<T>(request: () => Promise<T>): Promise<T> {
    const currentRequest = this.categoryRequestQueue.then(request, request);
    this.categoryRequestQueue = currentRequest.then(
      () => undefined,
      () => undefined,
    );
    return currentRequest;
  }

  private downloadExcel(data: Blob): void {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `NICE_SIM_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private toPartnerOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: `${item.name}${
          item.providerCode ? ` - ${item.providerCode.toUpperCase()}` : ""
        }`,
        value: item.code,
        type: item.type,
        isStore: item.isStore,
        providerCode: item.providerCode,
      })),
    ];
  }

  private toStoreOptions(items: any[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({
        label: item.store_name,
        value: item.store_code,
      })),
    ];
  }

  private toUserOptions(items: BaseUserResponseModel[] = []) {
    return [
      { label: strings.GlobalLabels.All, value: "" },
      ...items.map((item) => ({ label: item.name, value: item.id })),
    ];
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params, guard) => {
      try {
        guard?.runIfLatest(() => {
          this.setObservedState({
            loading: true,
            list: [],
            summary: {},
            pagination: {
              page: params.page ?? this.observedState.pagination.page,
              limit: params.limit ?? this.observedState.pagination.limit,
              total: this.observedState.pagination.total,
            },
          });
        });
        const res = await this.niceSimRegistrationUseCase.getList(params);
        guard?.runIfLatest(() => {
          if (res?.data) {
            this.setObservedState({
              list: res.data,
              pagination: res.pagination ?? {},
              summary: res.summary ?? {},
            });
          }
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.setObservedState({ list: [] }));
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.fetchNumberType, async (params, guard) => {
      guard?.runIfLatest(() => {
        this.setObservedState({ numberType: [] });
      });
      try {
        const res = await this.enqueueCategoryRequest(() =>
          this.niceSimRegistrationUseCase.getNiceSimCategory(
            params,
            "numberType",
          ),
        );
        guard?.runIfLatest(() => {
          this.setObservedState({ numberType: res ?? [] });
        });
      } catch (error) {
        console.error(error);
        if (guard?.isLatest()) {
          this.output.onError.next(error as Error);
        }
      }
    });

    this.skipFirstInitial(this.input.fetchDestiny, async (params) => {
      try {
        const res = await this.enqueueCategoryRequest(() =>
          this.niceSimRegistrationUseCase.getNiceSimCategory(params, "destiny"),
        );
        this.setObservedState({ destinyOptions: res ?? [] });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchNumerology, async (params) => {
      try {
        const res = await this.enqueueCategoryRequest(() =>
          this.niceSimRegistrationUseCase.getNiceSimCategory(
            params,
            "numerology",
          ),
        );
        this.setObservedState({ numerologyOptions: res ?? [] });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchZodiac, async (params) => {
      try {
        const res = await this.enqueueCategoryRequest(() =>
          this.niceSimRegistrationUseCase.getNiceSimCategory(params, "zodiac"),
        );
        this.setObservedState({ zodiacOptions: res ?? [] });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.niceSimRegistrationUseCase.getFilterList(
          params,
          "carrier",
        );
        this.setObservedState({ carrier: res ?? [] });
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);
        if (!isEmpty(cache)) {
          this.setObservedState({
            partnerOptions: this.toPartnerOptions(cache),
          });
          return;
        }
        const res = await this.niceSimRegistrationUseCase.getFilterList(
          params,
          "partner",
        );
        this.setObservedState({
          partnerOptions: this.toPartnerOptions(res ?? []),
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.setObservedState({ partnerOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      try {
        if (!params.partnerCode) return;
        const res = await this.niceSimRegistrationUseCase.getFilterList(
          params,
          "store",
        );
        this.setObservedState({ storeOptions: this.toStoreOptions(res ?? []) });
      } catch (error) {
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params.storeCode)) {
        this.setObservedState({ staffOptions: [] });
        return;
      }
      try {
        const res = await this.niceSimRegistrationUseCase.getUserForFilter(
          params,
          "staff",
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.name || user.userName || user.email || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          staffOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ staffOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      try {
        const filterType =
          params.providerCode === "mobitopup" || params.providerCode === "smm"
            ? "staff"
            : "userB2B";
        const res = await this.niceSimRegistrationUseCase.getUserForFilter(
          params,
          filterType,
        );
        const allUsers = (res ?? []).flatMap((item: any) => item?.users ?? []);
        const mappedUsers = allUsers.map((user: any) => ({
          id: user.id,
          name: user.fullName || user.email || user.name || user.phone || "",
          code: user.code,
        }));
        this.setObservedState({
          userOptions: this.toUserOptions(mappedUsers),
        });
      } catch (error) {
        this.setObservedState({ userOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.niceSimRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onDetailLoaded.next({});
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.selectDetail, (selection) => {
      if ("action" in selection) {
        this.setObservedState({ detail: selection.record });
        this.output.onDetailLoaded.next({ action: selection.action });
        return;
      }
      this.setObservedState({ detail: selection });
      this.output.onDetailLoaded.next({});
    });

    this.skipFirstInitial(this.input.syncFilterOptions, (options) => {
      this.setObservedState(options);
    });

    this.skipFirstInitial(this.input.exportExcel, async (params, guard) => {
      try {
        guard?.runIfLatest(() => this.setObservedState({ loading: true }));
        const res = await this.niceSimRegistrationUseCase.exportReport(params);
        if (res?.data) {
          if (res.data.size < 1000) {
            this.output.onError.next({
              message: strings.GlobalMessage.ExcelError,
            });
            return;
          }
          this.downloadExcel(res.data);
          this.output.onExportSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        guard?.runIfLatest(() => this.setObservedState({ loading: false }));
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: true },
        }));
        const res = await this.niceSimRegistrationUseCase.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error as Error);
      } finally {
        this.setObservedState((previous) => ({
          ...previous,
          statusLoading: { ...previous.statusLoading, [code]: false },
        }));
      }
    });
  }
}
